import { useRouter } from "@tanstack/react-router";

import Left from "@repo/icons/default/Left";
import Whatsapp from "@repo/icons/default/Whatsapp";

import { FlowHelpButton } from "@/components/ui/flow-header/flow-help-button";
import { ProgressBar } from "@/components/ui/progress-bar/progress-bar";
import { generateLivanceContactUrl } from "@/lib/helpers/contact-url";

import type { JSX } from "react";

// --- Sub-components ---

interface BackButtonProps {
  /** Custom back handler. Defaults to router.history.back() */
  onBack?: () => void;
}

const BackButton = ({ onBack }: BackButtonProps): JSX.Element => {
  const router = useRouter();

  const handleBack = (): void => {
    if (onBack) {
      onBack();
    } else {
      router.history.back();
    }
  };

  return (
    <button
      type="button"
      onClick={handleBack}
      aria-label="Voltar"
      className="border border-neutral-200 rounded-sm p-2"
    >
      <Left size={24} className="fill-neutral-700" />
    </button>
  );
};

const HelpButton = (): JSX.Element => {
  const handleHelp = (): void => {
    const url = generateLivanceContactUrl("whatsapp");

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <FlowHelpButton
      aria-label="Preciso de ajuda via WhatsApp"
      className="ml-auto"
      onClick={handleHelp}
    >
      <FlowHelpButton.Icon>
        <Whatsapp size={16} className="fill-secondary-400" />
      </FlowHelpButton.Icon>
      <FlowHelpButton.Label>Preciso de ajuda</FlowHelpButton.Label>
    </FlowHelpButton>
  );
};

interface ProgressBarSlotProps {
  /** Value from 0 to 1 representing progress in the flow */
  progress: number;
}

const ProgressBarSlot = ({ progress }: ProgressBarSlotProps): JSX.Element => (
  <ProgressBar progress={progress} variant="light" />
);

interface ActionsProps {
  children: React.ReactNode;
}

const Actions = ({ children }: ActionsProps): JSX.Element => (
  <div className="flex items-center justify-between gap-4">{children}</div>
);

interface TitleProps {
  title: string;
  description?: string;
}

const Title = ({ title, description }: TitleProps): JSX.Element => (
  <div className="flex min-w-px flex-1 flex-col">
    <p className="font-medium text-black text-lg leading-[1.32]">{title}</p>
    {description ? (
      <p className="text-neutral-700 text-xs leading-[1.32]">{description}</p>
    ) : null}
  </div>
);

// --- Root component ---

interface FlowHeaderProps {
  children?: React.ReactNode;
}

const FlowHeaderRoot = ({ children }: FlowHeaderProps): JSX.Element => (
  <div className="flex flex-col gap-4 px-4 pt-8 pb-4">{children}</div>
);

FlowHeaderRoot.Actions = Actions;
FlowHeaderRoot.Title = Title;
FlowHeaderRoot.BackButton = BackButton;
FlowHeaderRoot.HelpButton = HelpButton;
FlowHeaderRoot.ProgressBar = ProgressBarSlot;

export const FlowHeader = FlowHeaderRoot;
